/** Hex geometry for the 15x11 battlefield.
 *
 * The field is a flat plane of *congruent* cells, the way the classic's is. An earlier
 * version narrowed the back rows for perspective; the cells were then no longer
 * congruent, so their outlines had to be Voronoi regions clipped against six
 * unequal neighbours, and the result read on screen as a skewed field with a
 * ragged top edge rather than as a clean 15x11 board. Depth now comes from the
 * backdrop painting and from the contact shadows, which is where the classic gets it.
 *
 * A cell is a pointy-top hexagon, squashed vertically the way a ground plane
 * seen from a low angle is: the column pitch is `HEX_W`, the row pitch is
 * `ROW_PITCH`, odd rows step half a cell right, and the outline is
 *
 *     (0, -2p/3) (w/2, -p/3) (w/2, +p/3) (0, +2p/3) (-w/2, +p/3) (-w/2, -p/3)
 *
 * which tiles exactly: the lower-right edge of a cell is the upper-left edge of
 * the cell below-right of it, for any w and p. Every cell is that same stamp
 * translated, so there is one polygon and no per-cell clipping.
 *
 * Because the cells are squashed, the hexagon is not the Voronoi region of its
 * centre in screen space - the two cells diagonally below are nearer than the
 * two beside it. `pixelToHex` therefore un-squashes the point first (y is scaled
 * about the field so the lattice becomes regular), and in *that* space nearest
 * centre and the drawn hexagon are the same region, by construction. Drawing and
 * hit testing cannot drift apart.
 *
 * Neighbour order and the odd-row offset are read from the kernel
 * (`hexNeighbours`, which the pathfinder tie-breaks on), never re-derived.
 *
 * This module is pure arithmetic. It imports no renderer and no DOM, so the
 * test suite can exercise it in Node.
 */

import { BF_HEIGHT, BF_WIDTH, F_TWO_HEX, hexCol, hexIndex, hexRow } from "@game/core";
import type { BattleState, HexCoord } from "@game/core";
import { pixelPoint } from "../coords.js";
import type { PixelPoint } from "../coords.js";

// ------------------------------------------------------------------- brands

/**
 * The battle grid's own conversion site (I14). `HexCoord` and `PixelPoint` are
 * branded in `@game/core` and screen points are built by `coords.ts`; this file
 * is the only place in the battle screen that turns one into the other, which is
 * what makes "a function that takes a pixel cannot be handed a hex" true here.
 */
export const asHex = (n: number): HexCoord => n as HexCoord;

/** Arena space: the 1280x720 backdrop's own pixels, origin top-left. */
export const px = (x: number, y: number): PixelPoint => pixelPoint(x, y);

export const isHex = (n: number): boolean =>
  Number.isInteger(n) && n >= 0 && n < BF_WIDTH * BF_HEIGHT;

/** An axis-aligned box in arena pixels. */
export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** True when two boxes share area. Boxes that merely touch do not overlap. */
export function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/** The smallest box containing both. */
export function rectUnion(a: Rect, b: Rect): Rect {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, w: Math.max(a.x + a.w, b.x + b.w) - x, h: Math.max(a.y + a.h, b.y + b.h) - y };
}

/** True when `inner` lies entirely within `outer`. */
export function rectContains(outer: Rect, inner: Rect): boolean {
  return inner.x >= outer.x && inner.y >= outer.y
    && inner.x + inner.w <= outer.x + outer.w && inner.y + inner.h <= outer.y + outer.h;
}

// ---------------------------------------------------------------- constants

/** Native size of the `battlefield.*` backdrops. Everything here is in that space. */
export const ARENA_W = 1280;
export const ARENA_H = 720;

/**
 * Column pitch: the width of a cell, and the distance between two in a row.
 *
 * The tiling stamp below makes a cell `HEX_W` across and `4/3 * ROW_PITCH`
 * tall. A *regular* pointy-top hexagon would be `HEX_W = ROW_PITCH * 2/sqrt(3)`
 * and the cell would then be taller than it is wide, which is what a hex looks
 * like seen from directly above and not what one looks like painted on a ground
 * plane seen from a low angle. The classic's own battle hex has a 44px column pitch
 * and a 42px row pitch - a cell about 1.05 times wider than it is deep.
 *
 * That used to be cited to `refs/battle/expansion-battle-grassland-hexgrid-console-
 * 800x600.png`, which does not exist. Re-measured 2026-08-30 by autocorrelating
 * the edge profile of the two grid-on frames that do - `CLASSIC_Battlefield.jpg`
 * and a reference screenshot in the critic's reference set, both native 800x600 - the
 * column pitch is 43.3 and 44.0 and the row pitch is 42.0 on both. The pair
 * survives its own source going missing; only the citation changes.
 *
 * 60 across by `4/3 * 50 = 66.7` deep is 0.90 - a hair taller than wide, which
 * is the shape of the classic's own cell (44 across, 56 deep, 0.79) - and the board
 * is 930 of the arena's 1280 pixels: 85% of the 1088 inside the leather
 * flanks, which is what the classic's 682 of 800 is. The 79px left inside each
 * flank is where the commander stands.
 */
export const HEX_W = 60;
/**
 * Row pitch: the vertical distance between two rows' centres.
 *
 * **50, and it is set by the frame, not copied from the classic's 42.** The classic's 42 is
 * a pitch on a 556-row backdrop: eleven rows at 42 with the outline's extra
 * two thirds of a pitch come to 476 rows, 85.6% of the painting, and the field
 * runs from y=86 to y=548 with eight rows to spare above the console. Ours is a
 * 720-row arena with a 50-row console, so the same fraction of the same strip
 * is `0.856 * 670 = 573` rows, and `573 / 11.33 = 50.6`. At 42 the field was
 * 476 of 670 - two thirds - and the third it left was a band of empty ground
 * above the back rank that the classic simply does not have; the whole board, and
 * every creature scaled to it, read a fifth too small. At 1920x1080 a pitch of
 * 50 is 75 device rows, which is what the classic HD's own row pitch measures at that
 * resolution (the 800x556 plate scaled by 1.81).
 *
 * The pitch is the unit every creature is sized in (`unitTargetPitches`), so
 * moving it moves the roster with it: nothing else about a creature's size had
 * to change.
 */
export const ROW_PITCH = 50;
/**
 * Centre of the back row.
 *
 * The board is pushed as far DOWN its strip as the console allows, the way
 * The classic's is: everything above the back row is sky a creature can stand up
 * into, and everything below the front row is wasted.
 *
 *   top of the back row's outline     = ROW0_Y - HEX_HALF_H               =  97
 *   bottom of the front row's outline = ROW0_Y + 10*ROW_PITCH + HEX_HALF_H = 663
 *   console lip                       = ARENA_H - CONSOLE_H               = 670
 *   top of the tallest sprite = ROW0_Y + ROW_PITCH/2 - MAX_UNIT_H          =   1
 *
 * Seven rows of ground at the foot, against the classic's eight; the field's top edge
 * at 14.5% of the strip against the classic's 15.5% (86 of 556). The 97 rows above
 * the back row are not empty either: the plates paint their horizon in their
 * top ~70 rows, so a back-row creature's head and shoulders stand against the
 * far hills, which is exactly the classic picture. The tallest pose of the
 * tallest sheet reaches the top of the frame and no further - `UNIT_H_CEILING`
 * binds there - which is also what the classic does with its own tallest sheets.
 *
 * It was 190 with a pitch of 42, which put the field's top edge 162 rows down
 * and left a quarter of the strip as bare ground above the armies.
 */
export const ROW0_Y = 130;

/**
 * Height of chrome docked against the **top** edge, in arena pixels. Zero.
 *
 * It was 54, then 44, and both were a band running the full width of the frame
 * to carry the turn order. Measured against the references, at their native
 * sizes, the chrome a classic battle spends on horizontal bands is:
 *
 *   The classic the original          800x600    console 40-41   6.67-6.83%
 *   The classic dragons      800x600    console 40-41   6.67-6.83%
 *   The classic creature-info 800x600   console 40-41   6.67-6.83%
 *   The expansion               800x600    console 41-42   6.83-7.00%
 *   The classic HD          1920x1080   console 72-73   6.67-6.76%
 *
 * (Row of the strongest luma/texture step along the bottom of each reference,
 * `scratchpad/battle-finish/console_edge.py`; the detector lands on the row
 * below the gilt lip, so each is a lower bound by one or two rows. An
 * independent read of the same five put the band at 6.76-7.20%. Call it
 * **6.7-7.2% of frame height**, five references agreeing to within half a
 * percent.)
 *
 * An earlier version of this note carried a sixth row - `The expansion HD 986x650,
 * console 65, 65/650 = 10.0%` - and used it as the top of the reference band.
 * **That figure is wrong twice over and it is corrected here rather than
 * deleted, because it was quoted downstream.** The bottom 77px of that window
 * is not the battle console: it is the console *plus* the adventure shell's
 * status strip, which no other reference includes. And 650px of height is a
 * windowed screenshot, not a frame anyone plays at - chrome that is a fixed
 * number of pixels tall is flattered by a small window, so the percentage does
 * not survive normalising. The same UI at 1920x1080 is 6.9%. A percentage of a
 * 650px window may not be compared with a percentage of a 1080px one, and this
 * file did exactly that for a while.
 *
 * Not one of the five spends a pixel of *height* on a turn queue, and the two
 * that draw one at all run it down the side. Ours was 44 + 68 = 112 of 720,
 * 15.6%, against that 6.7-7.2% reference - the single largest quality gap left
 * on this screen and the one a critic scored the frame down for.
 *
 * A 16:9 frame's surplus is width, not height: the board is `FIELD_W` = 930 of
 * 1280 and leaves 175px of margin either side, which is where the queue goes
 * (`initiativeLayout` in `ui.ts`). Deleting the band took the chrome to
 * 68/720 = 9.4% - and 9.4% was still 36% over the reference, which is why
 * `CONSOLE_H` came down to 50 as well. Between them the chrome is now
 * 50/720 = 6.94%, inside the band. `arena.ts` drops the backdrop by exactly
 * this constant (`HORIZON_DROP`), so the backdrop sits where it was painted to
 * sit rather than 44px low.
 *
 * It stays a named constant rather than being deleted because the board still
 * has to be able to ask "how much of the top of the frame is not mine", and a
 * later design that wants a top edge again should move this number and not
 * re-derive one.
 */
export const TOPBAR_H = 0;

/**
 * Height of the command console, in arena pixels. It lives here rather than in
 * `ui.ts` because the arena has to know which strip of ground the chrome will
 * cover: scenery placed under it is sliced flat by the gilt lip, which is what
 * a row of half-drawn dead trees along the bottom edge was.
 *
 * 50, not 68. 68 was 9.44% of the frame against a five-reference band of
 * 6.7-7.2% (see `TOPBAR_H`) - 36% too tall, and the last horizontal band on the
 * screen that a critic could put a number to. 50/720 = 6.94% lands mid-band,
 * and at the shipped 1920x1080 it is 75 device pixels, which is what the classic HD's
 * own console measures at that exact resolution.
 *
 * The console's furniture is derived from this number rather than fixed beside
 * it (`BTN_H` in `ui.ts` is 88% of it, which is what the reference's buttons
 * are), so cutting it brings the buttons down with it instead of leaving them
 * growing out through the gilt lip.
 */
export const CONSOLE_H = 50;

/** Half-height of a cell outline: the tiling hexagon reaches 2/3 of a pitch. */
export const HEX_HALF_H = (ROW_PITCH * 2) / 3;

/** Full extent of the board, outline included. */
export const FIELD_W = HEX_W * (BF_WIDTH + 0.5);
export const FIELD_H = ROW_PITCH * (BF_HEIGHT - 1) + HEX_HALF_H * 2;
export const FIELD_LEFT = (ARENA_W - FIELD_W) / 2;
export const FIELD_TOP = ROW0_Y - HEX_HALF_H;

/** Centre of column 0 on an even row. Odd rows start half a cell to the right. */
const COL0_X = FIELD_LEFT + HEX_W / 2;

/**
 * How much smaller a prop standing on the back row is drawn than one on the
 * front row. The *cells* are congruent - flattening them is the whole point of
 * this rewrite - but an obstacle or a wall segment eleven rows away still wants
 * to be a little smaller, and that cue costs nothing because it never touches
 * the geometry the pointer is tested against.
 */
export const ROW_SCALE_BACK = 0.9;

/**
 * The unit a creature's drawn size is expressed in, and why it is not "percent
 * of screen height".
 *
 * This file used to state the target as a fraction of the frame's height, and
 * that unit is invalid for the comparison it was being used for. The classic's frame
 * is 600 tall with a 42px row pitch; ours is 720 tall with a 42px row pitch.
 * One percent of screen height is 0.143 pitches there and 0.171 here, so the
 * same percentage is a 20% bigger creature on our board. The quantity that
 * decides whether a creature's head reaches into the plaque of the stack one
 * row behind it is **row pitches**, and nothing else.
 *
 * Converted into pitches, the reference measurements are:
 *
 *   The expansion 800x600, grid drawn, column pitch 44, row pitch 42
 *     Plated Roller    90 x 57      1.36 pitches
 *     Steam Colossus  69 x 101     2.40
 *   The classic the original 800x600
 *     Glaivewarden   30 x 72      1.71
 *   The classic HD 1920x1080 (row pitch 75.6)
 *     Cave Crawler 1.79, Shriekwing 1.86, Ashfang Hound 2.06, Flamewalker 2.39,
 *     Umbral Wyrm 2.84
 *
 * So the classic draws a stack between 1.36 and 2.84 row pitches tall.
 *
 * **Those ten screenshot measurements have since been superseded by the whole
 * roster.** `sprite-critic` pulled the shipped `Standing` (idle-loop) sprite of
 * 77 creatures out of the game's own DEF exports at their native size - the
 * Archer is 40x76, the Angel 45x96, the Dwarf 36x63 - and those are pixels on
 * the same 42px row pitch this board uses, so `h / 42` is the reference in the
 * only unit that compares. Over all 77:
 *
 *   min 1.43 (Plated Roller)   median 2.10   max 3.21 (Reef Guard)
 *   per-tier means 1.87 1.94 1.91 2.05 2.15 2.43 2.55
 *
 * The band is therefore 1.43-3.21 and not 1.36-2.84. The old ceiling was set
 * from ten samples and clipped three creatures the full set says are legal
 * (Reef Guard 3.21, Blazewing 3.02, Mind Spirit 3.00); the old floor never bound
 * on anything. Neither bound binds under the ladder below, which is the shape a
 * cap should have - a bound that binds is a shelf.
 */
export const PITCH_MIN = 1.43;
export const PITCH_MAX = 3.21;

/**
 * The size ladder: how tall a creature is drawn, in row pitches, before its own
 * shape is taken into account.
 *
 * A single global scale on the art - which is what `UNIT_SCALE = 0.47` was -
 * does not decide anything about the creature. It re-exposes whatever scale
 * each sheet happened to be authored at, so the roster's apparent size ends up
 * a fact about the artist's canvas rather than a statement about the monster.
 * Measured on the shipped library under that scale, the resting frames came out
 * 1.66-3.27 pitches (median 2.57) - 15-22% over the reference band at the
 * median and 44% over at the top - and, worse, **out of order**:
 *
 *   Castle    Archer (tier 2) 2.86 pitches, High Radiant (tier 7) 2.73
 *   Greenmantle   Glade Runner (tier 1) 2.97,        Verdant Wyrm (tier 7) 2.63
 *
 * Those pairs stand in the same column, one above the other. A tier-7 dragon
 * that is smaller than a tier-1 glade runner is not a stylistic choice, it is an
 * artefact of two sheets being drawn on differently sized canvases.
 *
 * The reference's own ladder, from the classic HD frame, is monotone in tier and
 * nearly straight: Cave Crawler (Deepvault t1) 1.79, Shriekwing (t2) 1.86, Ashfang Hound
 * (Emberpit t3) 2.06, Flamewalker (t6) 2.39 - a slope of about 0.12 pitches per tier
 * off a 1.79 base - with the two-hex Umbral Wyrm (t7) at 2.84, which is 0.33
 * above where that line puts a one-hex tier 7.
 *
 * The constants below are that ladder's shape, set a little lower and a little
 * steeper. The reference line applied to our own 171 creatures puts the median
 * at 2.23 pitches and pushes six of them into the top clamp; these numbers put
 * the median at 2.09 - against a mean of 2.05 over the eight the classic stacks
 * measured above - and clamp **none of them**, which is the property that
 * matters most: every creature's size is then decided by the model rather than
 * by a bound, and a bound that binds is a shelf.
 *
 * The ladder is not made monotone by force, because the classic's is not: its
 * Plated Roller is a tier-3 drawn at 1.36 pitches and its Glaivewarden a tier-1 at
 * 1.71. What is asserted, in `battle-size-ladder.spec.ts`, is the failure that
 * was actually on screen - a faction's tier 7 drawn smaller than its tier 1 or
 * tier 2 - and not a total order the reference itself does not have.
 *
 * **Refitted against the 77-creature reference set** (see `PITCH_MAX`), which is
 * the first time this ladder has had per-creature ground truth rather than ten
 * screenshot measurements. The old numbers - 1.55 and 0.155 - were too low at
 * the bottom and too steep: the classic's own means are nearly flat across tiers 1-3
 * (1.87, 1.94, 1.91) and rise only at the top (2.43, 2.55), where ours climbed
 * a straight 0.155 a tier from a base a fifth of a pitch too low. Measured over
 * the 77 paired sheets, drawn height against the reference's:
 *
 *                        mean |err|   p90     max     Spearman   median pitches
 *   1.55 + 0.155/tier      12.3%     25.4%   38.3%     0.674         2.02
 *   1.73 + 0.115/tier       9.8%     22.0%   36.8%     0.686         2.13
 *   reference                 -         -       -         -          2.10
 *
 * The Spearman number is the honest limit and is worth stating plainly: **no
 * statistic in the content bundle predicts the classic's drawn size better than about
 * 0.70.** Measured against the same 77: tier 0.692, hp 0.699, damageMax 0.712,
 * aiValue 0.689, growth -0.695, and the creature's own drawn aspect ratio only
 * 0.105 - the reference's *own* aspect against its *own* pitch is 0.125. The
 * remaining third of the variance is how the artist drew that particular
 * monster and is not recoverable from anything the rules know, so a target of
 * 0.85 cannot be met from tier, footprint and art proportions and is not
 * chased here. What can be fixed is the level and the slope, and that is what
 * the refit does: a fifth off the mean error.
 *
 * **Raised again 2026-09-02, and this time on purpose above the DEF means.**
 * 1.73 + 0.115/tier reproduced the classic's *sprite* heights, and the composited
 * frame still read smaller than the game's: our resting pose is column 1
 * (`RESTING_POSE`), a turned stance whose trimmed box is the weapon as much as
 * the body - the Spearwarden's is 246 wide by 189 tall - so the *body* the ladder
 * bought was a fifth under the box it was sized on. Measured on the shipped
 * 1080p frame the fourteen fixture stacks stood 127-190 device rows against
 * The classic HD's 135-215 at the same resolution (a reference screenshot:
 * Cave Crawler 135, Shriekwing 140, Ashfang Hound 155, Flamewalker 180, Umbral Wyrm 215).
 * 1.85 + 0.135/tier puts a tier 1 at 1.85 pitches and a tier 7 at 2.66, and
 * the two-hex term takes a dragon to 2.96 before its own shape - inside `PITCH_MAX` and under the
 * frame's own cap on every fixture sheet, so the ladder still decides every
 * size and no bound is a shelf.
 */
export const TIER1_PITCHES = 1.85;
export const PITCHES_PER_TIER = 0.135;
/**
 * A creature that occupies two cells is a bigger animal, not a wider sprite.
 *
 * 0.1 rather than 0.2: the reference's two-hex creatures are not twice a tier
 * taller than its one-hex ones, and at 0.2 the term cost 0.5 points of mean
 * error over the 77. It is kept rather than deleted because it is the one place
 * the creature's *footprint* - which the rules know and the art does not - is
 * allowed to speak.
 */
export const TWO_HEX_PITCHES = 0.3;

/**
 * How much of the creature's own drawn shape is allowed to survive the ladder.
 *
 * Size in the classic is not purely a function of tier - the reference's clearest
 * single fact is that a quadruped Plated Roller (1.36 pitches) is *shorter* than a
 * Glaivewarden with a polearm two tiers below it (1.71). What separates them is
 * not the canvas they were drawn on, it is that one is long and low and the
 * other is tall and thin: a property of the art that survives any rescaling,
 * because it is a **ratio**.
 *
 * So the ladder is modulated by the resting frame's aspect, `h/w`, relative to
 * the library's median (measured over the 85 shipped sheets' `front.0` frames:
 * min 0.80, median 1.41, max 2.70). The exponent is the dial between two
 * defensible normalisations, and it is worth naming which: at 0 every creature
 * of a tier is drawn the same *height*; at 0.5 every creature of a tier is
 * drawn with the same `sqrt(w*h)`, i.e. the same silhouette *area*. The classic sits
 * between them - fitting the Plated Roller against the Glaivewarden, allowing for
 * their two tiers, puts it at about 0.4.
 *
 * The clamp is what keeps the ladder a ladder. One tier step is 6-9% of the
 * base, so an unbounded shape term would reorder the roster again through a
 * different mechanism; +-12% lets the art speak within about a tier and a half
 * and no further.
 *
 * **The exponent is 0.2, not 0.4, and the reason is a measurement that goes
 * against this paragraph's own argument.** Against the 77-creature reference
 * set, the drawn aspect ratio of our resting frames correlates with the classic's own
 * drawn height at Spearman 0.105 - and the reference's own aspect against its
 * own height at 0.125. Aspect does not predict size in the classic either. So every
 * point of exponent buys art-driven variety and pays for it in accuracy: over
 * the 77, 0.0 gives 9.4% mean error, 0.2 gives 9.8%, 0.3 gives 10.2% and 0.4
 * gives 11.4%. It is not set to zero, because zero draws every creature of a
 * tier at exactly the same height and that is the defect this whole file exists
 * to undo, just reached through a different door; 0.2 leaves a 3.8% coefficient
 * of variation inside a tier for four tenths of a point of mean error.
 *
 * `REF_ASPECT` is the median `h/w` of the 85 shipped resting frames, re-measured
 * against the current atlas (1.317; it was 1.41 before the sheets were re-keyed
 * against their un-quantised siblings and the silhouettes grew).
 */
export const REF_ASPECT = 1.317;
export const SHAPE_EXPONENT = 0.2;
export const SHAPE_MIN = 0.88;
export const SHAPE_MAX = 1.12;

/**
 * The most of its own authored size a sheet may be drawn at.
 *
 * The library is authored at roughly twice the size it is played at, so this
 * binds on nothing shipped (the largest scale the ladder asks for is 0.687, the
 * Moonsteed - it was 0.577 at a row pitch of 42, and the cap came up from
 * 0.62 with the pitch so that it would go on binding on nothing; 0.8 with the
 * 2026-09-02 ladder, for the same reason). It exists for the six neutrals that fall back to a small
 * map-object sprite: a 96px map icon asked to stand 2.4 pitches tall would be
 * magnified 105% and read as a blurred adventure token beside hand-cut battle
 * art. Nothing is ever drawn at more than three fifths of the art's own size.
 */
export const UNIT_SCALE_MAX = 0.8;

/**
 * Quantum a creature frame's scale is snapped down to.
 *
 * A sheet drawn at an arbitrary ratio drops source rows unevenly: a 1px spear
 * shaft survives on one row and vanishes on the next. This used to snap to an
 * exact integer reciprocal (1/4, 1/5, ...), but the *stage* is scaled by
 * `arenaScale` - 1.5 at 1080p, 1.0672 on the laptop - so the reciprocal never
 * survived to the device anyway, and the snap only ever cost up to a sixth of
 * the creature's height. A fine fixed quantum keeps the one property that is
 * real: the same creature is the same size on every frame and every machine.
 */
export const BLIT_QUANTUM = 256;

/** `want`, snapped down to the quantum. */
export function blitFraction(want: number): number {
  return Math.max(1 / BLIT_QUANTUM, Math.floor(want * BLIT_QUANTUM) / BLIT_QUANTUM);
}

/**
 * Clearance kept between the drawn boxes of two stacks. One pixel is enough to
 * make "disjoint" true rather than "touching"; it is not a design margin.
 */
const STACK_CLEARANCE = 1;

/**
 * How far a creature may stand out of its own cell, as a multiple of the band.
 *
 * This *was* 1: a stack was capped at one row pitch so that two stacks one row
 * apart could never touch, which made "nothing overlaps" true by arithmetic.
 * The cost was that every creature was drawn to fit inside a 60x51 box and the
 * board read as a set of tokens sitting in hexagons rather than as an army.
 *
 * The rule is now the one the classic itself uses: a creature is planted by its feet
 * and is allowed to be taller than the cell it stands on. What must stay inside
 * the hex is the *ground line*, not the silhouette - `stackSpriteRect` measures
 * up from `foot.ground`, so raising this grows a sprite upward and outward and
 * never moves the point it stands on.
 *
 * Overlap is therefore expected rather than prevented, and it is correct
 * because the painter's order already handles it: `stacks.ts` sets
 * `zIndex = round(groundY * 16)`, so the nearer stack is drawn over the farther
 * one and a front-row creature occludes the back-row creature behind it, which
 * is the depth cue the flat cells cannot give on their own.
 *
 * 3.3 rather than 3.75, and the change is worth stating precisely because it is
 * smaller than it looks: this cap is applied to the sheet's TALLEST pose, and
 * the resting height every creature is read at comes from the size ladder
 * (`unitTargetPitches`, clamped at `PITCH_MAX` = 3.21 pitches), not from here.
 * Measured on the five reference sheets in `battle-anim-scale.spec.ts`, moving
 * the cap from 156.5px to 137.6px changes four of the five drawn resting
 * heights by nothing at all and the Moonsteed' by 1.5% (82.1px to 80.9),
 * because on four of them the ladder already binds first.
 *
 * It comes down anyway, because a cap a whole row pitch above the tallest size
 * the ladder will ever ask for is not a cap. What it still governs is the swing
 * of a sheet whose animation frames tower over its idle - the Flamewalker Regent's
 * 329px tallest pose against its 296px idle - and 3.3 pitches is the top of the
 * 77-creature reference band (3.21) plus the clearance that band's own extreme
 * needs, rather than half a creature of slack.
 */
export const UNIT_OVERHANG = 3.3;

/**
 * The ceiling the window imposes, which is what actually decides the cap.
 *
 * A back-row stack's ground line is `ROW0_Y + ROW_PITCH / 2`, and it draws
 * upward from there, so the tallest sprite on the board reaches
 * `ROW0_Y + ROW_PITCH / 2 - MAX_UNIT_H`. That has to stay inside the frame:
 * stacks drawn off the top of the window is a defect this arena has already
 * shipped once, and it is the reason `ROW0_Y` is where it is.
 *
 * The overhang asks and the frame answers, and for a long time the answer was
 * 65 - about 1.27 row pitches, and a board on which every creature in the game
 * was drawn the same 65px tall. The note that stood here said raising it "needs
 * the bar to move or the field to be re-boxed", and both have since happened:
 * shortening the row pitch grew the sky above the back rank from 69px to 168,
 * and deleting the top band (`TOPBAR_H`) added the last 44. The answer was 210
 * for a while, with `UNIT_OVERHANG` the binding half of the pair.
 *
 * With the board filling its strip (`ROW0_Y` = 130, pitch 50) the answer is
 * 154, one pixel under `UNIT_OVERHANG`'s 164, and THIS is the binding half
 * again: the tallest pose of the tallest sheet on the back row reaches the top
 * of the frame exactly. That is the trade the classic makes too - its field starts
 * 86 rows down and its tallest sheets are drawn clipped against the window on
 * the back row - and it is the right side of it, because the size of every
 * creature is the pitch and the pitch is the field.
 */
export const UNIT_H_CEILING = ROW0_Y + ROW_PITCH / 2 - TOPBAR_H - STACK_CLEARANCE;

/**
 * The same allowance sideways.
 *
 * It was 1 - a creature had to fit its own cell across - on the argument that
 * two stacks side by side in a row share a ground line, so painter's order
 * cannot separate them, and horizontal overlap therefore reads as a collision
 * rather than as depth. The argument is sound and the conclusion still costs
 * more than it saves, because it is not free any more: under the size ladder
 * the widest drawn pose in the library is 148px across, so a one-cell width cap
 * would pull most of the roster back down to roughly the size it was, through
 * the width axis instead of the height one.
 *
 * 2.1 cells - 125px - is what the classic allows, and 2.5 was a fifth over its own
 * quoted evidence. Measured off the expansion reference at its native 800x600, where
 * the hex is 44px across: the Plated Roller is 90px wide, 2.05 hexes, and the
 * Steam Colossus 69px, 1.57. 2.05 is the widest creature in that reading, so the
 * cap is set just above it rather than half a cell above it.
 *
 * The floor is not taste either. The Elder Bone Sage's widest pose is 296px of art
 * and its resting scale is decided by the ladder at 105/256; a cap under 2.043
 * cells would take the width cap below that scale and shrink the creature's
 * RESTING height to pay for a pose that is on screen for a tenth of a second.
 * `battle-anim-scale.spec.ts` pins exactly that height, and pins it at equality
 * - the Bone Sage is the one sheet in the library with no slack at all.
 *
 * Same-row neighbours do still overlap, and that is a real cost, paid on the
 * rare board where two stacks stand shoulder to shoulder rather than on every
 * board.
 *
 * **Back to 2.5 with the row pitch at 50.** The 2.1 above was measured in
 * cells on a board whose cell was 1.43 pitches wide; at 60x50 a cell is 1.2
 * pitches wide, so 2.1 cells is 2.52 pitches where the classic's 2.05 cells of 44
 * are 2.15 pitches of 42 - and the roster is a fifth bigger. Held at 2.1 the
 * width cap, not the ladder, decided the size of every wide-posed sheet (the
 * Elder Bone Sage's 296px swing would have pulled its RESTING height down 14%).
 * 2.5 cells is 150px, three pitches, and the ladder binds first on every sheet
 * again, which is the property this file exists to keep.
 */
export const UNIT_WIDTH_OVERHANG = 2.5;

/**
 * Hard cap on how tall a stack may be drawn, in arena pixels.
 *
 * Not the no-overlap rule - see `UNIT_OVERHANG` - and not the size rule either,
 * which is `unitTargetPitches`. It is the frame's cap: the tallest pose of the
 * tallest sheet has to stay inside the window.
 */
export const MAX_UNIT_H = Math.min(
  ROW_PITCH * UNIT_OVERHANG - STACK_CLEARANCE,
  UNIT_H_CEILING,
);

/**
 * Hard cap on how wide a stack may be drawn, per cell it stands on.
 */
export const MAX_UNIT_W = HEX_W * UNIT_WIDTH_OVERHANG - STACK_CLEARANCE;

/**
 * Obstacle art is authored at map-object size. 0.85, up from 0.7 with the row
 * pitch: a classic obstacle is a one-to-three-cell object drawn at the same
 * scale as the creatures beside it, and at 0.7 a boulder on the field stood
 * 45 rows against a spearwarden's 100. The two caps in `placeObstacles` (the
 * frame's height, `OBSTACLE_REACH_MAX` cells across) still bind the large
 * sheets.
 */
export const OBSTACLE_SCALE = 0.85;

const N_HEXES = BF_WIDTH * BF_HEIGHT;

/**
 * Un-squash factor. Scaling y by this about the field's origin turns the
 * squashed lattice into a regular one, where nearest-centre is exactly the drawn
 * hexagon. `pixelToHex` is the only caller.
 */
const UNSQUASH = (HEX_W * Math.sqrt(3)) / 2 / ROW_PITCH;

// ------------------------------------------------------------------- layout

/** Depth cue for props standing on a row. Never used by the cell geometry. */
export function rowScale(row: number): number {
  return ROW_SCALE_BACK + (1 - ROW_SCALE_BACK) * (row / (BF_HEIGHT - 1));
}

/**
 * Centre of cell (col,row) for any integer pair, in-range or not. Odd rows step
 * half a cell to the right, which is the offset `hexNeighbours` assumes.
 */
function centreOf(col: number, row: number): { readonly x: number; readonly y: number } {
  return {
    x: COL0_X + col * HEX_W + ((row & 1) === 1 ? HEX_W * 0.5 : 0),
    y: ROW0_Y + row * ROW_PITCH,
  };
}

/**
 * The six neighbour cells of (col,row), unclipped, in the kernel's order:
 * up-left, up-right, right, down-right, down-left, left. `hexNeighbours` drops
 * the ones off the board; drawing needs them anyway, to bound the border cells.
 */
export function neighbourColRow(col: number, row: number, dir: number): readonly [number, number] {
  const dx = (row & 1) === 1 ? 0 : -1;
  switch (dir) {
    case 0: return [col + dx, row - 1];
    case 1: return [col + dx + 1, row - 1];
    case 2: return [col + 1, row];
    case 3: return [col + dx + 1, row + 1];
    case 4: return [col + dx, row + 1];
    default: return [col - 1, row];
  }
}

/** Direction index (0..5, kernel order) from `from` to an adjacent `to`, or -1. */
export function directionBetween(from: HexCoord, to: HexCoord): number {
  const c = hexCol(from), r = hexRow(from);
  for (let d = 0; d < 6; d++) {
    const [nc, nr] = neighbourColRow(c, r, d);
    if (nc < 0 || nr < 0 || nc >= BF_WIDTH || nr >= BF_HEIGHT) continue;
    if (hexIndex(nc, nr) === (to as number)) return d;
  }
  return -1;
}

// ------------------------------------------------------------------- tables

/** The one cell outline, as offsets from a centre. Every cell is this stamp. */
const STAMP: readonly number[] = [
  0, -HEX_HALF_H,
  HEX_W / 2, -HEX_HALF_H / 2,
  HEX_W / 2, HEX_HALF_H / 2,
  0, HEX_HALF_H,
  -HEX_W / 2, HEX_HALF_H / 2,
  -HEX_W / 2, -HEX_HALF_H / 2,
];

/**
 * Everything the screen may know about one cell.
 *
 * **This is the single source of hex geometry.** `hexCentre`, `hexPolygon`,
 * `hexGroundLine`, `hexBand` and `hexBounds` are accessors on this record, not
 * five separate derivations, and the marker, the sprite seat, the obstacle
 * anchors and `pixelToHex` all read it. The last two rounds each shipped a
 * marker that was out against the sprite it marked - first by 43px, then by a
 * whole cell - and both times the cause was two expressions for the same
 * lattice drifting apart. There is one expression now and it is precomputed
 * once per cell, so "the marker outline is the cell outline" is true by
 * identity rather than by two formulas agreeing.
 */
export interface HexGeometry {
  readonly hex: HexCoord;
  /** Centre of the cell, in arena pixels. */
  readonly centre: PixelPoint;
  /** The cell outline, flat `[x0,y0,x1,y1,...]`, six vertices. */
  readonly polygon: readonly number[];
  /** Where a sprite standing here puts its feet: the front edge of the band. */
  readonly seat: PixelPoint;
  /** The `HEX_W` x `ROW_PITCH` strip the cell owns; these tile without overlap. */
  readonly band: Rect;
  /** Axis-aligned bounds of `polygon`. Computed from the polygon, not restated. */
  readonly bounds: Rect;
}

function boundsOfPoly(poly: readonly number[]): Rect {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (let i = 0; i < poly.length; i += 2) {
    const x = poly[i]!, y = poly[i + 1]!;
    if (x < x0) x0 = x;
    if (x > x1) x1 = x;
    if (y < y0) y0 = y;
    if (y > y1) y1 = y;
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

const CELLS: readonly HexGeometry[] = ((): HexGeometry[] => {
  const out: HexGeometry[] = [];
  for (let h = 0; h < N_HEXES; h++) {
    const c = centreOf(hexCol(h), hexRow(h));
    const poly = new Array<number>(STAMP.length);
    for (let i = 0; i < STAMP.length; i += 2) {
      poly[i] = c.x + STAMP[i]!;
      poly[i + 1] = c.y + STAMP[i + 1]!;
    }
    out.push({
      hex: asHex(h),
      centre: px(c.x, c.y),
      polygon: poly,
      seat: px(c.x, c.y + ROW_PITCH / 2),
      band: { x: c.x - HEX_W / 2, y: c.y - ROW_PITCH / 2, w: HEX_W, h: ROW_PITCH },
      bounds: boundsOfPoly(poly),
    });
  }
  return out;
})();

/** The one geometry record for a cell. Every other query here reads it. */
export function hexGeometry(h: HexCoord): HexGeometry {
  return CELLS[h as number]!;
}

const cell = (h: HexCoord): HexGeometry => CELLS[h as number]!;

// ------------------------------------------------------------------ queries

/** Centre of a cell, in arena pixels. */
export function hexCentre(h: HexCoord): PixelPoint {
  return cell(h).centre;
}

/**
 * Where a sprite standing on this cell puts its feet: the **front edge of the
 * cell's row band**, half a pitch below the centre.
 *
 * It used to be 0.24 of a pitch below the centre, which is neither the cell's
 * centre nor an edge of anything, and left a stack's box straddling the band
 * boundary. On the band edge the whole figure - feet on the line, head at most
 * `MAX_UNIT_H` above it - lies inside its own row's strip, and two rows cannot
 * reach each other however the kernel deals them out. The feet land 8px above
 * the cell's bottom vertex, so the creature still stands inside its outline
 * rather than on top of the row in front.
 */
export function hexGroundLine(h: HexCoord): PixelPoint {
  return cell(h).seat;
}

/**
 * The strip of field a cell owns: `HEX_W` by `ROW_PITCH`, centred on the cell.
 *
 * The hexagons interlock, so two cells in adjacent rows share screen area and
 * cannot bound what stands on them. These bands do not: rows tile them exactly
 * and columns tile them exactly, so two *different* cells have disjoint bands.
 * Everything a stack draws is kept inside its own band, and "no stack overlaps
 * another" then holds by construction rather than by inspection.
 */
export function hexBand(h: HexCoord): Rect {
  return cell(h).band;
}

/** The cell outline, flat [x0,y0,x1,y1,...]. Exactly the region `pixelToHex` returns. */
export function hexPolygon(h: HexCoord): readonly number[] {
  return cell(h).polygon;
}

/** Axis-aligned bounds of the cell outline. */
export function hexBounds(h: HexCoord): Rect {
  return cell(h).bounds;
}

/** Depth cue for whatever stands on this cell. The cells themselves are equal. */
export function hexRowScale(h: HexCoord): number {
  return rowScale(hexRow(h));
}

/** Cell width, for sizing anything that must fit a cell. */
export function hexWidth(_h: HexCoord): number {
  return HEX_W;
}

export function hexToPixel(h: HexCoord): PixelPoint {
  return hexCentre(h);
}

/**
 * Which cell contains this point, or null. Nearest centre in un-squashed space
 * is exactly the drawn hexagon, so this and `hexPolygon` cannot disagree; the
 * polygon test then rejects points that fell off the board entirely.
 */
export function pixelToHex(p: PixelPoint): HexCoord | null {
  const uy = (p.y - ROW0_Y) * UNSQUASH;
  let best = -1;
  let bestD = Infinity;
  for (let i = 0; i < N_HEXES; i++) {
    const c = CELLS[i]!.centre;
    const dx = p.x - c.x;
    const dy = uy - (c.y - ROW0_Y) * UNSQUASH;
    const d = dx * dx + dy * dy;
    if (d < bestD) { bestD = d; best = i; }
  }
  if (best < 0) return null;
  const g = CELLS[best]!;
  if (Math.abs(p.x - g.centre.x) > HEX_W / 2 || Math.abs(p.y - g.centre.y) > HEX_HALF_H) return null;
  return pointInPoly(g.polygon, p.x, p.y) ? asHex(best) : null;
}

function pointInPoly(poly: readonly number[], x: number, y: number): boolean {
  let inside = false;
  const n = poly.length >> 1;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = poly[i * 2]!, yi = poly[i * 2 + 1]!;
    const xj = poly[j * 2]!, yj = poly[j * 2 + 1]!;
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}


// -------------------------------------------------------- stack footprint

/**
 * Size of the count plaque. It lives here rather than in `stacks.ts` because
 * the plaque is part of what a stack draws, and what a stack draws has to be
 * proved to stay inside its own band.
 */
export const BADGE_W = 28;
/**
 * 16, and the number is the NUMERAL's, not the plate's own taste.
 *
 * It was 12 - "28 x 10 of coloured fill, 30 x 12 counting the frame", read off
 * the reference - and that was measured against a nine-row `text` face which
 * the device-pixel floor has since stopped delivering: on a 1080-tall window
 * every reading is held to 15 device rows (`deviceCapFloor`), the arena is
 * 1.5 device pixels per arena pixel, and 15 device rows of digit is exactly
 * the 10 arena rows the field had. Measured on the shipped 1920x1080 shot at
 * 2026-09-08: plaque interior rows 216-230, glyph rows 217-231, so the last
 * row of the digits was painted ON the plate's bottom rule, which is gold for
 * its whole width, and the tops touched the top rule at 1366x768. A numeral
 * with no plate around it has stopped being a plaque.
 *
 * The reference insets its own digits: on `battle/a reference screenshot
 * dragons.jpg` the `28` plaque frames rows 959-960 and 978, interior 962-975,
 * digits 963-974 - one row of clearance above and three below, and no glyph
 * pixel anywhere on the frame. Sixteen arena rows is a 13-row clean field
 * (the rule and its shade take three), which holds the promoted numeral with
 * clearance at all four combinations this build is shot at: 19.5 device rows
 * of field for 15 of digit at 1920x1080, 15.6 for 15 at 1920x1080 under an
 * 80% page, 13.9 for 11 at 1366x768, 11.1 for 11 at 1366x768 at 80%.
 */
export const BADGE_H = 16;

/**
 * The cells a stack stands on and the box it may draw in.
 *
 * **This is the one function.** The sprite, its contact shadow, its count
 * plaque, its active marker and the selection hex drawn round it all come from
 * here, because the last time they did not the gold hex was drawn on the head
 * cell of a two-hex Sunscale Wyrm while the dragon itself was drawn from the
 * midpoint of its two cells - the marker landed half a hex up and left of the
 * creature it was marking, and only the dragon's front claws were inside it.
 * A marker and a sprite that read the grid through different expressions will
 * drift again; there is nothing to keep them together but this.
 */
export interface StackFootprint {
  /** Head cell first, then the tail cell of a two-hex stack. */
  readonly hexes: readonly HexCoord[];
  /**
   * How many cells the stack actually stands on: 1 or 2. Read this rather than
   * the two-hex *flag* when sizing anything, because a two-hex stack whose tail
   * would fall off the board stands on one cell and may only draw on one.
   */
  readonly cells: number;
  /** The union of those cells' bands. Nothing the stack draws leaves it. */
  readonly band: Rect;
  /** Centre of the footprint: what a marker is drawn around. */
  readonly centre: PixelPoint;
  /** Where the feet go: centred on the footprint, on the band's front edge. */
  readonly ground: PixelPoint;
}

/**
 * A two-hex stack occupies `hex` and the cell one column *behind* it, which is
 * `hexCol(hex) - facing` - the kernel's own rule (`startHex`), not a guess. A
 * tail off the board is ignored, so a footprint is never wider than the field.
 */
export function stackHexes(hex: number, facing: number, twoHex: boolean): readonly HexCoord[] {
  const head = asHex(hex);
  if (!twoHex) return [head];
  const tailCol = hexCol(hex) - facing;
  if (tailCol < 0 || tailCol >= BF_WIDTH) return [head];
  return [head, asHex(hexIndex(tailCol, hexRow(hex)))];
}

export function stackFootprint(hex: number, facing: number, twoHex: boolean): StackFootprint {
  const hexes = stackHexes(hex, facing, twoHex);
  let band = hexBand(hexes[0]!);
  for (let i = 1; i < hexes.length; i++) band = rectUnion(band, hexBand(hexes[i]!));
  return {
    hexes,
    cells: hexes.length,
    band,
    centre: px(band.x + band.w / 2, band.y + band.h / 2),
    ground: px(band.x + band.w / 2, band.y + band.h),
  };
}

/**
 * The footprint of a live stack, read straight off the kernel.
 *
 * The two-hex answer comes from `F_TWO_HEX` and from nothing else - not from
 * how wide the sprite came out, not from a copy of the flag cached in a view.
 * This is the only place the battle screen asks "does this stack stand on two
 * cells", so the marker and the creature cannot disagree about it.
 */
export function stackFootprintOf(state: BattleState, slot: number): StackFootprint {
  return stackFootprint(
    state.stacks.hex[slot]!,
    state.stacks.facing[slot]!,
    (state.stacks.flags[slot]! & F_TWO_HEX) !== 0,
  );
}

// --------------------------------------------------------- marker geometry

/**
 * The cell outlines the selection marker paints: exactly the cells the stack
 * stands on, exactly as `hexPolygon` draws them.
 *
 * A one-hex stack gets one hexagon and a two-hex stack gets the two it
 * occupies. Nothing here consults a sprite size, so a creature drawn small
 * inside a two-cell footprint still gets the two-cell marker its rules say it
 * has, and a one-hex creature can never be ringed across its neighbour.
 */
export function markerPolygons(f: StackFootprint): readonly (readonly number[])[] {
  return f.hexes.map((h) => hexPolygon(h));
}

/** The footprint's outer boundary, so two cells are ringed once, not twice. */
export function markerEdges(f: StackFootprint): readonly number[][] {
  return regionEdges(f.hexes as readonly number[]);
}

/**
 * Bounds of what the marker outlines. Equal to `hexBounds` for a one-hex stack
 * and to the union of the two cells' bounds for a two-hex one, by construction:
 * both come from the same polygons the marker strokes.
 */
export function markerBounds(f: StackFootprint): Rect {
  let r = hexBounds(f.hexes[0]!);
  for (let i = 1; i < f.hexes.length; i++) r = rectUnion(r, hexBounds(f.hexes[i]!));
  return r;
}

/**
 * The two frame sizes a sheet's scale is decided from.
 *
 * `idle*` is the resting `front.0` pose - the silhouette a player looks at for
 * all but a second or two of a battle, and the one every reference measurement
 * above was taken on, so it is what the size target is set against.
 *
 * `max*` is the largest of the sheet's twelve frames on each axis, and it is
 * only ever a cap. The shipped sheets vary enormously pose to pose - one frame
 * of the Tidemaid Oracle's twelve is 2.47x wider than her idle, and the Pegasus' widest
 * wing-beat 1.70x taller - so a scale read off the frame on screen changes the
 * creature's size every time it swings. One scale per sheet, set on the resting
 * pose and capped on the widest: "the same creature is the same size on every
 * frame", which is what `BLIT_QUANTUM` claims and could not deliver alone.
 */
export interface UnitSheetSize {
  readonly idleW: number;
  readonly idleH: number;
  readonly maxW: number;
  readonly maxH: number;
  /**
   * The frame the stack actually **stands on** between events, when that is not
   * `front.0`.
   *
   * It usually is not any more. `RESTING_POSE` in `keys.ts` is `ready`, because
   * `front.0` is the one frame in every sheet that faces the camera square on -
   * upper-body mirror-IoU median 0.714 over the library against the classic's own
   * idle at 0.586, and `front.1` at 0.609 - and a battlefield where both armies
   * stare out of the screen is the defect that measurement found.
   *
   * The two frames then do two different jobs. `idle*` still sets the *target*,
   * because the shape term is a claim about the creature's proportions and
   * `front.0` is the neutral stance that states them; `rest*` is the divisor,
   * because the drawn height the target is about is the height of the frame on
   * screen. Omit it and the resting frame is `front.0`, which is what every
   * caller written before `RESTING_POSE` existed meant.
   */
  readonly restW?: number;
  readonly restH?: number;
}

/**
 * How tall a creature is drawn, in row pitches: the ladder, modulated by the
 * resting frame's own proportions, clamped into the classic's measured band.
 *
 * Everything here is a fact about the *creature* - what tier it is, how many
 * cells it stands on, whether it is long and low or tall and thin. Nothing here
 * is a fact about the sheet's canvas, which is the whole point: a creature's
 * apparent size must not be an artefact of the size its artist drew it at.
 */
export function unitTargetPitches(
  tier: number, cells: number, idleW: number, idleH: number,
): number {
  const t = Math.max(1, Math.min(7, Math.round(tier)));
  const base = TIER1_PITCHES + PITCHES_PER_TIER * (t - 1)
    + (cells > 1 ? TWO_HEX_PITCHES : 0);
  const aspect = Math.max(0.05, idleH) / Math.max(1, idleW);
  const shape = Math.min(
    SHAPE_MAX, Math.max(SHAPE_MIN, Math.pow(aspect / REF_ASPECT, SHAPE_EXPONENT)),
  );
  return Math.min(PITCH_MAX, Math.max(PITCH_MIN, base * shape));
}

/**
 * The scale every frame of one creature's sheet is drawn at.
 *
 * The target comes from `unitTargetPitches` and is applied to the resting
 * frame; the two caps are the frame's, applied to the sheet's largest pose, so
 * no pose can breach them. Snapped down to the blit quantum last.
 */
export function unitDrawScale(sheet: UnitSheetSize, tier: number, cells: number): number {
  const restH = sheet.restH === undefined || sheet.restH <= 0 ? sheet.idleH : sheet.restH;
  const want = (unitTargetPitches(tier, cells, sheet.idleW, sheet.idleH) * ROW_PITCH)
    / Math.max(1, restH);
  return blitFraction(Math.min(
    want,
    UNIT_SCALE_MAX,
    MAX_UNIT_H / Math.max(1, sheet.maxH),
    (MAX_UNIT_W + (cells - 1) * HEX_W) / Math.max(1, sheet.maxW),
  ));
}

/**
 * The box a stack's sprite covers. `anchorX` is the frame's own anchor as a
 * fraction of its width (every shipped unit frame is centre-bottom, but the six
 * neutrals that fall back to a map-object sprite are not necessarily).
 */
export function stackSpriteRect(
  f: StackFootprint, drawnW: number, drawnH: number, anchorX = 0.5,
): Rect {
  return { x: f.ground.x - drawnW * anchorX, y: f.ground.y - drawnH, w: drawnW, h: drawnH };
}

/**
 * Where the count plaque goes: on the ground line, **beside** the stack's feet
 * rather than on them, offset toward the lower right the way the classic's is.
 *
 * Four versions, and the reference settles which is right.
 *
 * A plaque hung on the hex's lower-right *corner* sat at the centre height of
 * the cell below-right of it, which with adjacent rows in play is exactly where
 * the head of the next stack arrives: the Angels' `42` was painted across the
 * Master Bowyer's face. The fix was to clamp it into the stack's own band.
 *
 * The band's bottom edge IS the ground line, so the clamp put the plate on top
 * of the sprite's feet: every figure on the board ended flat at the plate's top
 * edge, with its ankles and the near half of its contact shadow painted over.
 *
 * Both files this used to be measured on -
 * A reference screenshot and
 * A reference screenshot - do not exist,
 * and with them went the two per-creature readings that used to be quoted here
 * (a Steam Colossus's feet at y=460 under a plate spanning y=449..461; a
 * Glaivewarden's plate starting 15px right of his shoulder). Neither creature
 * appears in any reference on disk, so **those two are gone and are not
 * replaced.**
 *
 * What is replaceable is the geometry they were quoted to support. Re-measured
 * 2026-08-30 on three frames in the critic's reference set, by connected
 * components of the plate's player-coloured fill, rescaled to native 800x600:
 *
 *   CLASSIC_Battlefield.jpg   8 plates   median 27.8 x  9.9
 *   a reference screenshot          10 plates   median 27.8 x  9.0
 *   a reference screenshot          10 plates   median 25.9 x 10.1
 *
 * So the plate is about **28 x 10** of coloured fill, or 30 x 12 counting its
 * gilt frame - not the 32x13 that stood here. The nine plates found in `classic16`
 * fall into two vertical phases 16px apart, one per army, and within each phase
 * every plate's bottom row is an exact multiple of the 42px row pitch away from
 * the others: the plate is pinned to the row's ground line, not to the sprite's
 * own extent. And it is pushed sideways clear of the body on every frame - in
 * `classic16` visibly so, beside the Skyclaw, the Serpent Dancers and the Watcher Orb alike.
 *
 * A fourth reading, taken 2026-09-01 on this workstream's own reference frame
 * (`rig/ref/battle.png`, an 800x600 shot at 0.8875), settles the side the plate
 * goes on, which none of the three above did. Six defender plates span x
 * 594..614 with their creatures' bodies at x 615..660 - the plate immediately
 * LEFT of the body - while every attacker plate (the peasants' `4`, the
 * High Radiant's `25`, the Lancer's `14`) sits immediately RIGHT of a body on
 * the left of it. The plate is on the side the creature faces, not on a fixed
 * side of the screen.
 *
 * The vertical position was never the problem.
 *
 * **The push is gone, and that is the fourth version.** "Left edge past the
 * sprite's own half-width, capped at 0.6 cells" put the plate 36 to 64 pixels
 * from its own axis - 0.60 to 1.07 cell widths - which is entirely outside the
 * cell it belongs to and squarely on the art of the stack in the next column or
 * the next row. On lava a Doom Rider's `38` lay across the barding of the
 * Doom Rider behind it; on highlands a `42` lay across a Longbowman's bow
 * arm. A count that is not inside its own hex is not a label, because the
 * player has to guess which creature it counts.
 *
 * The rule now is the reference's, and it is a corner rather than a clearance:
 * bottom edge on the ground line, outer edge on the edge of the stack's own
 * band, on the side the stack faces. That is where the classic puts it - measured on
 * the reference frame, every attacker's plate sits immediately right of its
 * creature and every defender's immediately left of its own, both toward the
 * middle of the field - and it is the same corner of the same cell on every
 * stack, every row and both armies, which is what makes the association
 * readable without a leader line.
 *
 * `drawnW` only ever pulls the plate IN, and on a one-cell stack it cannot move
 * it at all. A plate pushed off the sprite lands somewhere different for every
 * sheet, which is the failure the paragraph above is about; but the cell corner
 * on its own has a failure of its own on a **two-hex** stack, and it was on
 * screen in all seven arenas. The footprint is then two cells - 120px - while
 * the art centred on it is not: the Doom Rider's resting frame draws about 84
 * arena pixels across, so its `38` hung 18px clear of the horse's nose, in the
 * middle of an empty neighbouring cell, and the `24` of the stack behind it did
 * the same one row up. Two plates floating in open ground between two armies
 * are not labels either.
 *
 * **The fifth version pushes it clear again, and this time the reference was
 * measured rather than remembered.** `min(band.w / 2, max(HEX_W / 2, drawnW /
 * 2))` is 30 for every one-cell stack, which is the cell's own edge - and a
 * creature is one to one and a half cells wide, so the plate landed *on the
 * creature it counts* on every one-hex stack in the game. A critic measured it
 * on the shipped frame: 6 of the 7 plaques a side sat on art, the `12` on the
 * Skyclaw's own front paw, the `4` on the Glaivewarden's shield, the `18` on the
 * Lancer's hoof.
 *
 * The classic puts the plate in the NEXT CELL. On `expansion-gallery_wasteland_battle3`,
 * which draws the lattice, every plate sits in the hex beside its creature's -
 * the Knight's `15`, the Lancer's `14`, the Sentinels' `8` and `7` - with its
 * bottom edge on the ground line and its inner edge clear of the silhouette.
 * That is what the third version was reaching for when it pushed "past the
 * sprite's own half-width, capped at 0.6 cells"; what it got wrong was the CAP,
 * which let a plate travel to the next column's axis and land on another
 * stack's art.
 *
 * So: the plate clears its own creature (`drawnW / 2 + BADGE_W`), floored at
 * half a cell so a narrow one does not climb onto its own axis, and capped
 * short of the next column's axis (`band.w / 2 + HEX_W / 2 + BADGE_W / 2 - 2`,
 * which is the bound `battle-geometry.spec.ts` asserts, less two pixels). The
 * cap binds only on the widest sheets - a Skyclaw drawing 100 arena pixels
 * across keeps 7px of overlap at its outer edge - and the floor binds on
 * nothing. `resolveBadgeSides` still moves the plate to the other corner when
 * the rank in front stands on this one.
 *
 * Two stacks in the same column are a row pitch apart and the plate is shorter
 * than that, so no two plates meet.
 */
export function stackBadgeRect(f: StackFootprint, drawnW: number, facing = 1): Rect {
  // Outside the creature, and outside its cell when that is what it takes.
  const clear = Math.max(HEX_W / 2, drawnW / 2 + BADGE_W);
  const cap = f.band.w / 2 + HEX_W / 2 + BADGE_W / 2 - 2;
  const reach = Math.min(cap, clear);
  return {
    x: Math.round(facing >= 0 ? f.centre.x + reach - BADGE_W : f.centre.x - reach),
    y: Math.round(f.ground.y - BADGE_H),
    w: BADGE_W,
    h: BADGE_H,
  };
}

/** Everything one stack puts on the field: the sprite and its count plaque. */
export function stackDrawnRect(
  f: StackFootprint, drawnW: number, drawnH: number, anchorX = 0.5, facing = 1,
): Rect {
  return rectUnion(
    stackSpriteRect(f, drawnW, drawnH, anchorX),
    stackBadgeRect(f, drawnW, facing),
  );
}

/**
 * How far outside the board's edge the commander's stand point is, in cells.
 * See `heroStandPoint` for why it is this and not more.
 */
export const HERO_INSET_CELLS = 1.1;
/*
 * **1.1 is a floor, and it was tested for.** `FRAME_W` came up from 84 to 94 to
 * cut the board's dead margin (see its note), and the obvious way to pay for
 * more would be to slide the commander in - but `battle-composition.spec.ts`
 * holds him 20 arena px clear of column 0's cell, which is `60 * inset - 44.8
 * >= 20`, i.e. `inset >= 1.08`. Tried at 1.0 and the assertion caught it at
 * 15.2 px. The bound is not taste: at 0.76 an High Radiant standing on column 0
 * crossed the horse's head with a wing tip.
 */

/**
 * Where a commanding hero stands: outside the board on his own side, level with
 * the second rank.
 *
 * The classic puts the hero on the battlefield as a figure, not only as a face on the
 * console - the original, the expansion and HD references all show a mounted commander at the
 * outer edge beyond the stack column, and we drew nothing there at all. He
 * stands off the board, so this is deliberately not a hex: nothing can be
 * ordered onto his square and the pointer never resolves to him.
 */
export function heroStandPoint(side: 0 | 1): PixelPoint {
  // The commander is drawn at `HERO_SCALE` = 1.4 off a 64px frame, so he
  // reaches 44.8 either side of this point. At 1.1 cells the attacker's box
  // runs 64.2..153.8: 21 px clear of column 0's cell (175) and 29.8 px UNDER
  // the leather (`FRAME_W` = 94), which is chrome above him (`Arena.chrome`)
  // and crops the horse's rump the way the window edge does on
  // `expansion-gallery_battle_plated_roller_1` (x 0..60). It was 0.76, the most the
  // leather allowed with the frame under the army, and at 0.76 an High Radiant
  // on column 0 crossed the horse's head with a wing tip: the classic keeps the hero a
  // clear ~90 device px from the column-0 stack
  // (a reference screenshot, hero x240-340, stack centre x430). A
  // column-0 stack whose ART overhangs its cell can still cross him, which
  // is why `stacks.ts` sorts the commander *in front* of the army rather than
  // by his ground line - see `HERO_Z`.
  const inset = HEX_W * HERO_INSET_CELLS;
  return px(
    side === 0 ? FIELD_LEFT - inset : FIELD_LEFT + FIELD_W + inset,
    ROW0_Y + ROW_PITCH,
  );
}

/** Where the acting-stack marker hangs: the top of its own band, on its axis. */
export function stackMarkerAnchor(f: StackFootprint): PixelPoint {
  return px(f.centre.x, f.band.y);
}

/**
 * How much air the acting-stack chevron needs above the point it hangs from.
 *
 * The chevron is drawn upward from its anchor - see `paintActiveMarker` - so
 * the anchor is its TIP, and this is the rest of it. It used to be drawn
 * downward from the anchor, which is why a marker hung "above the head" came
 * down 25px into the creature and collided with every raised weapon on the
 * board; the anchor was right and the art was upside down.
 */
export const MARKER_HEADROOM = 16;

/**
 * Where a marker that has to clear the creature's head hangs: the point its tip
 * comes down to.
 *
 * The band's top edge is one row pitch above the feet, which was above the head
 * of a 65px sprite and is now somewhere around its knees. A marker that means
 * "this one is acting" has to be above the silhouette or it reads as jewellery.
 *
 * Clamped so the marker's own body, not merely its tip, stays inside the frame:
 * the tallest sheet standing on the back row reaches within a pixel of the top,
 * and a clamp that only kept the tip on screen cut the chevron in half on the
 * one creature whose marker matters most.
 */
export function stackMarkerHeadAnchor(f: StackFootprint, drawnH: number, gap = 6): PixelPoint {
  return px(
    f.centre.x,
    Math.max(TOPBAR_H + MARKER_HEADROOM + gap, f.ground.y - drawnH - gap),
  );
}

// ------------------------------------------------------------ region edges

/** Which two polygon vertices bound the edge a cell shares with neighbour `d`. */
const EDGE_OF_DIR: readonly (readonly [number, number])[] = [
  [5, 0], [0, 1], [1, 2], [2, 3], [3, 4], [4, 5],
];

/**
 * The *outline* of a set of cells: every edge with no member of the set on its
 * far side, as flat `[x0,y0,x1,y1]` segments.
 *
 * Used for the board's own footprint - the permanent edge that tells a player
 * where the arena stops and the scenery margin begins - and available to any
 * overlay that wants a region rather than a wireframe.
 */
export function regionEdges(hexes: Iterable<number>): readonly number[][] {
  const set = new Set<number>(hexes);
  const out: number[][] = [];
  for (const h of set) {
    if (!isHex(h)) continue;
    const poly = hexPolygon(asHex(h));
    const col = hexCol(h);
    const row = hexRow(h);
    for (let d = 0; d < 6; d++) {
      const [nc, nr] = neighbourColRow(col, row, d);
      const inside = nc >= 0 && nr >= 0 && nc < BF_WIDTH && nr < BF_HEIGHT
        && set.has(hexIndex(nc, nr));
      if (inside) continue;
      const [a, b] = EDGE_OF_DIR[d]!;
      out.push([poly[a * 2]!, poly[a * 2 + 1]!, poly[b * 2]!, poly[b * 2 + 1]!]);
    }
  }
  return out;
}

/** The whole 15x11 board's outline. Constant, so it is traced once. */
export const BOARD_EDGES: readonly number[][] = regionEdges(
  Array.from({ length: N_HEXES }, (_, i) => i),
);

/**
 * **Every** cell edge on the board - interior included - each traced once.
 *
 * `regionEdges` gives the *outline* of a set and deliberately drops the shared
 * edges inside it, which is right for a footprint and wrong for a grid. The
 * obvious alternative, stroking all 165 cell polygons, draws every interior
 * edge twice: on a 1px antialiased line two coincident passes are visibly
 * heavier than one, so the board would carry a heavy lattice inside a lighter
 * rim. Deduplicating on the segment's two endpoints gives one pass per edge and
 * a little under half the geometry - 165*6 = 990 sides collapse to `length`
 * below, which `battle-grid.spec.ts` pins.
 *
 * Endpoints are compared at 3 decimal places because the two cells sharing an
 * edge compute its ends from different centres; the coordinates agree to well
 * inside that (`HEX_W` and `ROW_PITCH` are integers and the stamp's offsets are
 * thirds of a pitch), and rounding is what makes the key an identity rather
 * than a float comparison.
 */
export const BOARD_CELL_EDGES: readonly number[][] = (() => {
  const seen = new Set<string>();
  const out: number[][] = [];
  for (let h = 0; h < N_HEXES; h++) {
    const poly = hexPolygon(asHex(h));
    for (let i = 0; i < 6; i++) {
      const j = (i + 1) % 6;
      const ax = poly[i * 2]!, ay = poly[i * 2 + 1]!;
      const bx = poly[j * 2]!, by = poly[j * 2 + 1]!;
      const a = `${ax.toFixed(3)},${ay.toFixed(3)}`;
      const b = `${bx.toFixed(3)},${by.toFixed(3)}`;
      const key = a < b ? `${a}|${b}` : `${b}|${a}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push([ax, ay, bx, by]);
    }
  }
  return out;
})();

// ---------------------------------------------------------------- animation

/**
 * Straight-line interpolation between two cells' ground lines. Movement follows
 * the kernel's path hex by hex, so a straight segment per step is correct: the
 * curve comes from the path, not from the tween.
 */
export function lerpGround(a: HexCoord, b: HexCoord, t: number): PixelPoint {
  const ga = hexGroundLine(a), gb = hexGroundLine(b);
  return px(ga.x + (gb.x - ga.x) * t, ga.y + (gb.y - ga.y) * t);
}

/**
 * Round to whole device pixels. Every sprite position passes through this: a
 * sprite left on a fractional coordinate samples between texels and the art goes
 * soft, which on 16-bit-style pixel work is immediately visible.
 */
export function snap(v: number, deviceScale: number): number {
  return Math.round(v * deviceScale) / deviceScale;
}

/**
 * The scale at which the arena fills the viewport.
 *
 * It used to be `Math.floor`, whole numbers only, so a 1920x1080 window drew the
 * 1280x720 arena at 1 inside a 320px black surround - the battlefield read as a
 * postcard on a table. Both target viewports are 16:9, where filling is exact
 * (1.5 and 1.0672); off 16:9 the arena is scaled to cover and the overflow is
 * cropped, but never past the point where the board itself would be cut, so a
 * tall window shows a little background either side instead of eating the field.
 *
 * **1.5 is a half-integer and that costs real crispness**: an authored 1px
 * feature is worth one and a half device pixels, so a diagonal or a curve
 * alternates between one and two along its own run, and a creature texel sits
 * on a 3:2 lattice. It is not fixable from here, and the two integers either
 * side are both worse. Floor gives 1.0, which is the 320px surround this
 * function exists to have removed. Ceil gives 2.0, which cannot be reached at
 * all: `keepField` at 1920x1080 is `min(1920/970, 1080/596)` = 1.812, so
 * scaling to 2 would crop 174 of the field's 476 pixels of depth - four of its
 * eleven rows - off the bottom of the window. Text is already immune (see
 * `snapMagnification`, which pins every glyph texel to a whole number of device
 * pixels regardless of what this returns); the art is not.
 */
/**
 * How the arena is placed in a CSS viewport.
 *
 * `compactChrome` is the landscape-phone path: the HTML touchbar owns Wait /
 * Defend / Strike, so the painted console is cropped off and the hex field is
 * allowed to fill the leftover height instead of reserving 120 arena rows of
 * chrome padding.
 */
export interface ArenaFit {
  readonly compactChrome?: boolean;
}

export interface ArenaPlacement {
  readonly scale: number;
  readonly x: number;
  readonly y: number;
  readonly view: Rect;
}

function arenaScaleFor(viewportW: number, viewportH: number, compact: boolean): number {
  const cover = Math.max(viewportW / ARENA_W, viewportH / ARENA_H);
  // Never crop so hard that a cell, or the chrome that hugs the field, is lost.
  // Compact drops the console-band padding: the field is the thing to keep.
  const padW = compact ? 8 : 40;
  const padH = compact ? 8 : 120;
  const keepField = Math.min(viewportW / (FIELD_W + padW), viewportH / (FIELD_H + padH));
  return Math.min(cover, keepField);
}

export function arenaScale(viewportW: number, viewportH: number, fit: ArenaFit = {}): number {
  return arenaScaleFor(viewportW, viewportH, fit.compactChrome === true);
}

/**
 * The part of the arena a viewport actually shows, in arena pixels. Chrome that
 * must hug the edge of the *window* - the initiative bar, the command bar - is
 * laid out against this rather than against the 1280x720 page, so nothing ends
 * up drawn in a cropped strip.
 */
export function arenaViewRect(
  viewportW: number,
  viewportH: number,
  fit: ArenaFit = {},
): Rect {
  return arenaFit(viewportW, viewportH, fit).view;
}

/** Scale, letterbox origin, and visible arena rect for a CSS viewport. */
export function arenaFit(
  viewportW: number,
  viewportH: number,
  fit: ArenaFit = {},
): ArenaPlacement {
  const compact = fit.compactChrome === true;
  const scale = arenaScaleFor(viewportW, viewportH, compact);
  let x = (viewportW - ARENA_W * scale) / 2;
  let y = (viewportH - ARENA_H * scale) / 2;
  if (compact) {
    // Pin the hex field in the window: crop sky and the painted console, not
    // a rank of cells. When the field is taller than the viewport, keep the
    // front rank (the one the console used to cover).
    const fieldTop = FIELD_TOP * scale;
    const fieldBot = (FIELD_TOP + FIELD_H) * scale;
    const fieldH = fieldBot - fieldTop;
    y = fieldH <= viewportH
      ? (viewportH - fieldH) / 2 - fieldTop
      : viewportH - fieldBot;
  }
  x = Math.round(x);
  y = Math.round(y);
  return {
    scale,
    x,
    y,
    view: { x: -x / scale, y: -y / scale, w: viewportW / scale, h: viewportH / scale },
  };
}

// ------------------------------------------------------- attack direction

/**
 * Which legal stand hex the pointer is asking for, and which of the six sides of
 * the target that is. The angle from the target's centre to the pointer picks
 * the side; the nearest legal stand hex in angle wins, so a cursor dragged round
 * a target walks the attacker round it. Returns null when nothing is legal.
 */
export function attackApproach(
  target: HexCoord,
  pointer: PixelPoint,
  legalFrom: readonly number[],
): { readonly hex: HexCoord; readonly dir: number } | null {
  const c = hexCentre(target);
  const want = Math.atan2(pointer.y - c.y, pointer.x - c.x);
  let bestHex = -1;
  let bestDir = -1;
  let bestDelta = Infinity;
  for (const h of legalFrom) {
    const dir = directionBetween(target, asHex(h));
    if (dir < 0) continue;
    const hc = hexCentre(asHex(h));
    const a = Math.atan2(hc.y - c.y, hc.x - c.x);
    let d = Math.abs(a - want);
    if (d > Math.PI) d = 2 * Math.PI - d;
    if (d < bestDelta) { bestDelta = d; bestHex = h; bestDir = dir; }
  }
  if (bestHex < 0) return null;
  return { hex: asHex(bestHex), dir: bestDir };
}
